import axios from 'axios';
import { config } from '../assets/config/config';

const apiUrl = config.api.url;

/**
 * Process a gamification event
 * @param {Object} params - Event parameters
 * @param {string} params.userId - User ID
 * @param {string} params.action - Action type (e.g., 'DOCUMENT_SCAN', 'AI_DETECTION', 'HUMANIZATION', 'PLAGIARISM_CHECK')
 * @param {Object} params.metadata - Additional metadata for the action
 * @param {number} params.metadata.wordCount - Number of words processed
 * @returns {Promise<Object>} - Gamification updates
 */
const processEvent = (params) => {
  return axios.post(`${apiUrl}/v1/gamification/process`, params);
};

/**
 * Get all available badges and their requirements
 * @returns {Promise<Object>} - Available badges
 */
const getBadges = () => {
  return Promise.resolve({
    data: {
      badges: [
        // Mastery Badges
        { 
          id: 'word_wizard', 
          name: 'Word Wizard', 
          description: 'Process thousands of words', 
          secret: false,
          tiers: [
            { tier: 1, requirement: 'Process 1,000 words', threshold: 1000 },
            { tier: 2, requirement: 'Process 2,000 words', threshold: 2000 },
            { tier: 3, requirement: 'Process 5,000 words', threshold: 5000 },
            { tier: 4, requirement: 'Process 10,000 words', threshold: 10000 },
            { tier: 5, requirement: 'Process 100,000 words', threshold: 100000 }
          ]
        },
        { 
          id: 'consistency_champion', 
          name: 'Consistency Champion', 
          description: 'Maintain your daily streak',
          secret: false, 
          tiers: [
            { tier: 1, requirement: '3-day streak', threshold: 3 },
            { tier: 2, requirement: '7-day streak', threshold: 7 },
            { tier: 3, requirement: '14-day streak', threshold: 14 },
            { tier: 4, requirement: '30-day streak', threshold: 30 },
            { tier: 5, requirement: '100-day streak', threshold: 100 }
          ]
        },
        { 
          id: 'document_detective', 
          name: 'Document Detective', 
          description: 'Scan multiple documents',
          secret: false, 
          tiers: [
            { tier: 1, requirement: 'Scan 3 documents', threshold: 3 },
            { tier: 2, requirement: 'Scan 5 documents', threshold: 5 },
            { tier: 3, requirement: 'Scan 10 documents', threshold: 10 },
            { tier: 4, requirement: 'Scan 20 documents', threshold: 20 },
            { tier: 5, requirement: 'Scan 50 documents', threshold: 50 },
            { tier: 6, requirement: 'Scan 100 documents', threshold: 100 },
            { tier: 7, requirement: 'Scan 500 documents', threshold: 500 },
            { tier: 8, requirement: 'Scan 1,000 documents', threshold: 1000 }
          ]
        },
        
        // Feature-specific badges with consistent tiers (1, 3, 7, 10, 50, 100)
        { 
          id: 'ai_detective', 
          name: 'AI Detective', 
          description: 'Detect AI-generated content in increasing amounts of text',
          secret: false, 
          tiers: [
            { tier: 1, requirement: 'Detect AI in 500 words', threshold: 500 },
            { tier: 2, requirement: 'Detect AI in 2,000 words', threshold: 2000 },
            { tier: 3, requirement: 'Detect AI in 5,000 words', threshold: 5000 },
            { tier: 4, requirement: 'Detect AI in 10,000 words', threshold: 10000 },
            { tier: 5, requirement: 'Detect AI in 50,000 words', threshold: 50000 },
            { tier: 6, requirement: 'Detect AI in 100,000 words', threshold: 100000 }
          ]
        },
        { 
          id: 'humanization_expert', 
          name: 'Humanization Expert', 
          description: 'Humanize increasing amounts of AI-generated text',
          secret: false, 
          tiers: [
            { tier: 1, requirement: 'Humanize 500 words', threshold: 500 },
            { tier: 2, requirement: 'Humanize 2,000 words', threshold: 2000 },
            { tier: 3, requirement: 'Humanize 5,000 words', threshold: 5000 },
            { tier: 4, requirement: 'Humanize 10,000 words', threshold: 10000 },
            { tier: 5, requirement: 'Humanize 50,000 words', threshold: 50000 },
            { tier: 6, requirement: 'Humanize 100,000 words', threshold: 100000 }
          ]
        },
        { 
          id: 'plagiarism_hunter', 
          name: 'Plagiarism Hunter', 
          description: 'Check increasing amounts of text for plagiarism',
          secret: false, 
          tiers: [
            { tier: 1, requirement: 'Check 500 words for plagiarism', threshold: 500 },
            { tier: 2, requirement: 'Check 2,000 words for plagiarism', threshold: 2000 },
            { tier: 3, requirement: 'Check 5,000 words for plagiarism', threshold: 5000 },
            { tier: 4, requirement: 'Check 10,000 words for plagiarism', threshold: 10000 },
            { tier: 5, requirement: 'Check 50,000 words for plagiarism', threshold: 50000 },
            { tier: 6, requirement: 'Check 100,000 words for plagiarism', threshold: 100000 }
          ]
        },
        
        { 
          id: 'speed_demon', 
          name: 'Speed Demon', 
          description: 'Complete multiple scans quickly',
          secret: false, 
          tiers: [
            { tier: 1, requirement: 'Complete 2 scans in under 1 hour', threshold: 2 },
            { tier: 2, requirement: 'Complete 5 scans in under 1 hour', threshold: 5 },
            { tier: 3, requirement: 'Complete 10 scans in under 1 hour', threshold: 10 }
          ]
        },
        
        // Behavior Badges
        { 
          id: 'weekend_warrior', 
          name: 'Weekend Warrior', 
          description: 'Scan documents on 3 consecutive weekends',
          secret: false,
          tiers: [{ tier: 1, requirement: 'Scan on 3 consecutive weekends', threshold: 3 }]
        },
        { 
          id: 'full_spectrum', 
          name: 'Full Spectrum', 
          description: 'Use all major features at least once',
          secret: false,
          tiers: [{ tier: 1, requirement: 'Try all features', threshold: 1 }]
        },
        { 
          id: 'perfect_week', 
          name: 'Perfect Week', 
          description: 'Maintain streak for a complete calendar week',
          secret: false,
          tiers: [{ tier: 1, requirement: 'Complete a full week streak', threshold: 7 }]
        },
        { 
          id: 'comeback_kid', 
          name: 'Comeback Kid', 
          description: 'Return after 7+ days inactive',
          secret: false,
          tiers: [{ tier: 1, requirement: 'Return after a break', threshold: 1 }]
        },
        { 
          id: 'quick_draw', 
          name: 'Quick Draw', 
          description: 'Complete 3 scans in under 5 minutes',
          secret: false,
          tiers: [{ tier: 1, requirement: '3 scans in 5 minutes', threshold: 3 }]
        },
        { 
          id: 'deep_dive', 
          name: 'Deep Dive', 
          description: 'Analyze a document with 5000+ words',
          secret: false,
          tiers: [{ tier: 1, requirement: 'Scan a 5000+ word document', threshold: 5000 }]
        },
        
        // New feature-focused badges
        { 
          id: 'triple_threat', 
          name: 'Triple Threat', 
          description: 'Master all three main features by processing text in each',
          secret: false,
          tiers: [
            { tier: 1, requirement: 'Process 1,000 words with each feature', threshold: 1000 },
            { tier: 2, requirement: 'Process 5,000 words with each feature', threshold: 5000 },
            { tier: 3, requirement: 'Process 20,000 words with each feature', threshold: 20000 }
          ]
        },
        { 
          id: 'balanced_analyzer', 
          name: 'Balanced Analyzer', 
          description: 'Use all three main features in a balanced way',
          secret: false,
          tiers: [{ tier: 1, requirement: 'Process at least 3,000 total words distributed across all features', threshold: 3000 }]
        },
        
        // Secret Badges (only show after unlocked)
        { 
          id: 'settings_savvy', 
          name: 'Settings Savvy', 
          description: 'Customize 5+ settings options',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 5 }]
        },
        { 
          id: 'profile_perfectionist', 
          name: 'Profile Perfectionist', 
          description: 'Complete all profile fields',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 1 }]
        },
        { 
          id: 'easter_egg_hunter', 
          name: 'Easter Egg Hunter', 
          description: 'Find hidden features',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 1 }]
        },
        { 
          id: 'night_shift', 
          name: 'Night Shift', 
          description: 'Use the app between 12-4am',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 1 }]
        },
        { 
          id: 'weekend_binge', 
          name: 'Weekend Binge', 
          description: 'Complete 10+ scans in a single weekend day',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 10 }]
        },
        { 
          id: 'completionist', 
          name: 'Completionist', 
          description: 'View every page in the app at least once',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 1 }]
        },
        
        // Secret feature-specific badges with unique rewards
        { 
          id: 'ai_nemesis', 
          name: 'AI Nemesis', 
          description: 'Find extremely high AI probability content',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 1 }]
        },
        { 
          id: 'humanization_wizard', 
          name: 'Humanization Wizard', 
          description: 'Humanize a document and get it past AI detection',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 1 }]
        },
        { 
          id: 'plagiarism_guardian', 
          name: 'Plagiarism Guardian', 
          description: 'Identify a document with significant plagiarism',
          secret: true,
          tiers: [{ tier: 1, requirement: '???', threshold: 1 }]
        }
      ]
    }
  });
};

export const gamificationService = {
  processEvent,
  getBadges
}; 